import React, { memo, useEffect, useRef, useState } from 'react'
import {
  Center,
  Text,
  Flex,
  IconButton,
  Tag,
  TagCloseButton,
  TagLabel,
  TagLeftIcon,
  useColorModeValue,
  useMediaQuery,
  Image,
  Box,
  Skeleton
} from '@chakra-ui/react'

import { Formik, FormikProps, Form } from 'formik'
import { VscSearch } from 'react-icons/vsc'
import { FiMapPin } from 'react-icons/fi'

import { MotionFlex, SuggestionBox } from 'components'
import { PropertyCategory, useSearchLocationQuery } from 'generated/graphql'
import { generatePrices } from 'utils'
import { omit, uniqBy } from 'lodash'
import { SearchInitialValues, SearchTab } from 'types'
import {
  LAST_SELECTED_FILTERS_STORAGE_KEY,
  MAX_ROOM_AMOUT,
  RECENT_SEARCHED_LOCATIONS_STORAGE_KEY
} from '../../../constants'
import { StateFormIndicator } from 'components/FormElements'
import * as Yup from 'yup'
import { useHistory } from 'react-router'
import { useBrowserStorage, useGeolocation, useIsMounted } from 'hooks'
import { SearchInitialValues as InitialValues } from 'types'
import { useAppContext } from 'context/AppProvider'
import { useAuthContext } from 'context/AuthProvider'
import { AdvancedSearchResults, SearchBarInput } from '..'
import { useTranslation } from 'react-i18next'
import CustomTooltip from 'components/CustomTooltip'
import Filters from '../Filters'
import { PrimaryButton } from 'components/Buttons'
import { images, theme } from 'theme'
import { FaSearchLocation } from 'react-icons/fa'

type SearchBarProps = {
  rooms?: number
  prices?: number[]
  tab: PropertyCategory
  shouldShowSearch?: boolean
  initialValuesData: InitialValues
  isResult?: boolean
}
const SearchBarFormValidation = Yup.object().shape({
  selectedArea: Yup.array()
})

const LIMIT = 3

const SearchBar: React.FC<SearchBarProps> = ({
  rooms,
  prices,
  tab,
  isResult,
  initialValuesData,
  shouldShowSearch
}) => {
  const borderTheme = useColorModeValue('gray.100', 'gray.700')
  const [isTabletOrMobile] = useMediaQuery('(max-width: 40em)')

  const redirection = (area: SearchTab) => {
    switch (area?.typename) {
      case 'Province':
        history.push(
          isAuthenticated
            ? `/auth/simple-search/${tab.slug}/${area.slug}`
            : `/simple-search/${tab.slug}/${area.slug}`,
          area
        )
        break
      case 'City':
        history.push(
          isAuthenticated
            ? `/auth/simple-search/${tab.slug}/${area.province?.slug}/${area.district?.slug}/${area.slug}`
            : `/simple-search/${tab.slug}/${area.province?.slug}/${area.district?.slug}/${area.slug}`,
          area
        )
        break
      case 'District':
        history.push(
          isAuthenticated
            ? `/auth/simple-search/${tab.slug}/${area.province?.slug}/${area.slug}`
            : `/simple-search/${tab.slug}/${area.province?.slug}/${area.slug}`,
          area
        )
        break
      case 'Suburb':
        history.push(
          isAuthenticated
            ? `/auth/simple-search/${tab.slug}/${area.city?.district?.province?.slug}/${area.city?.district?.slug}/${area.city?.slug}/${area.slug}`
            : `/simple-search/${tab.slug}/${area.city?.district?.province?.slug}/${area.city?.district?.slug}/${area.city?.slug}/${area.slug}`,
          area
        )
        break
      default:
        return
    }
  }
  const history = useHistory()
  const state = history.location.state as { quickSearch?: boolean }

  const locationData = useGeolocation()

  const [searchTerm, setSearchTerm] = useState('')
  const { data: results, loading: resultsLoading } = useSearchLocationQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      searchTerm,
      locationData: omit(locationData, ['currency'])
    }
  })

  const [searchFocus, setSearchFocus] = useState(false)
  const searchInput = useRef<HTMLInputElement>(null)
  const [localRecentSearch, setLocalRecentSearch] = useBrowserStorage<SearchTab[]>(
    RECENT_SEARCHED_LOCATIONS_STORAGE_KEY,
    'local',
    []
  )
  const { isAuthenticated } = useAuthContext()
  const [localLastSelectedFilters, setLocalLastSelectedFilters, removeLocalLastSelectedFilters] =
    useBrowserStorage<InitialValues & Pick<SearchBarProps, 'tab'>>(
      LAST_SELECTED_FILTERS_STORAGE_KEY,
      'local'
    )
  const isMounted = useIsMounted()
  const { setsearchFilters } = useAppContext()
  useEffect(() => {
    setsearchFilters({ ...(localLastSelectedFilters as SearchInitialValues) })
  }, [localLastSelectedFilters])
  const formikRef = useRef<FormikProps<InitialValues>>(null)
  const removeFilters = () => {
    formikRef.current?.resetForm()
    removeLocalLastSelectedFilters()
    window.location.reload()
  }
  const { t } = useTranslation()

  const searchBarBg = useColorModeValue('white', 'gray.700')

  return (
    <Formik
      innerRef={formikRef}
      validationSchema={SearchBarFormValidation}
      initialValues={initialValuesData}
      validateOnChange
      onSubmit={async (values, { setStatus, setErrors, setSubmitting }) => {
        try {
          const { selectedArea } = values
          setStatus(null)
          setSubmitting(true)
          if (selectedArea.length > 0) {
            if (isMounted) {
              setsearchFilters({ ...values, tab })
              setLocalLastSelectedFilters({ ...values, tab })
              if (selectedArea.length > 1) {
                localRecentSearch?.concat(selectedArea)
                if (localRecentSearch && localRecentSearch?.length > 10) localRecentSearch.pop()
                setLocalRecentSearch(uniqBy(localRecentSearch, '_key'))
                history.push(
                  isAuthenticated
                    ? `/auth/advanced-search/${tab.slug}`
                    : `/advanced-search/${tab.slug}`,
                  selectedArea
                )
              } else {
                const area: SearchTab = selectedArea?.[0]
                localRecentSearch?.unshift(area)
                if (localRecentSearch && localRecentSearch?.length > 10) localRecentSearch.pop()
                setLocalRecentSearch(uniqBy(localRecentSearch, '_key'))

                redirection(area)
              }
            }
          } else
            setErrors({
              selectedArea: 'Veuillez entrer une ville ou un quartier.'
            })
        } catch (error: any) {
          setStatus(error?.message)
        }
        setSubmitting(false)
      }}
    >
      {({
        values: {
          maxPrice,
          minPrice,
          bedRooms,
          search,
          bathRooms,
          rentalTerms,
          propertyAvailabilities,
          furnished,
          propertyTypes,
          selectedArea
        },

        errors,
        setFieldValue,
        isSubmitting
      }: FormikProps<InitialValues>) => {
        const isDirty = formikRef.current?.dirty

        return (
          <Form autoComplete="off" style={{ width: '100%' }}>
            <Flex align="flex-start">
              <Box flex={2.1}>
                <Flex
                  flex={1}
                  border="1px solid"
                  borderColor={borderTheme}
                  align={isTabletOrMobile ? 'flex-end' : 'center'}
                  borderRadius="lg"
                  boxShadow={theme.boxShadow2}
                  bg={searchBarBg}
                  mb={4}
                  position="relative"
                >
                  {!isTabletOrMobile && (
                    <CustomTooltip label="Search">
                      <IconButton
                        size="md"
                        position="absolute"
                        height="100%"
                        isLoading={isSubmitting}
                        type="submit"
                        w={16}
                        color="gray.500"
                        variant="ghost"
                        borderRadius="5px 0 0 5px"
                        aria-label="Change Theme"
                        icon={<FaSearchLocation />}
                      />
                    </CustomTooltip>
                  )}
                  <Flex
                    align="center"
                    direction={isTabletOrMobile ? 'column' : 'row'}
                    ml={isTabletOrMobile ? 4 : 16}
                    pl={isTabletOrMobile ? 0 : 2}
                    wrap="wrap"
                    flex={1}
                  >
                    <Flex flexWrap="wrap" w={isTabletOrMobile ? '100%' : 'auto'}>
                      {selectedArea.length > LIMIT && (
                        <Center mr={2}>
                          <Text fontSize="xs">{selectedArea.length - LIMIT}+</Text>
                        </Center>
                      )}
                      {selectedArea
                        ?.slice(selectedArea.length >= LIMIT ? selectedArea.length - LIMIT : 0)
                        ?.map((item) => (
                          <Skeleton key={item._key} mr={1} my={2} isLoaded={Boolean(item)}>
                            <CustomTooltip
                              label={`${item.name}${item.city ? ', ' + item.city.name : ''}${
                                item.province ? ', ' + item.province.name : ''
                              } (${item.typename})`}
                            >
                              <Tag
                                size="md"
                                variant="subtle"
                                p={3}
                                colorScheme="blue"
                                minW="fit-content"
                              >
                                <TagLeftIcon boxSize="10px" as={FiMapPin} />
                                <TagLabel
                                  cursor="pointer"
                                  fontSize="xs"
                                  fontWeight="semibold"
                                  onClick={() => {
                                    redirection(item)
                                  }}
                                >
                                  {item.name}
                                </TagLabel>
                                <TagCloseButton
                                  onClick={() => {
                                    setFieldValue('selectedArea', [
                                      ...selectedArea.filter((i) => item._key !== i._key)
                                    ])
                                    ;(searchInput.current as any).focus()
                                  }}
                                />
                              </Tag>
                            </CustomTooltip>
                          </Skeleton>
                        ))}
                    </Flex>

                    <Flex width={isTabletOrMobile ? '100%' : undefined} flex={1}>
                      <SearchBarInput
                        variant="unstyled"
                        fontSize="sm"
                        fontWeight="400"
                        position="relative"
                        onBlur={() => {
                          const int = setInterval(() => {
                            setSearchFocus(false)
                            clearInterval(int)
                          }, 150)
                        }}
                        onFocus={() => setSearchFocus(true)}
                        isLoading={resultsLoading}
                        name="search"
                        ref={searchInput}
                        py={isTabletOrMobile ? 2 : 1}
                        placeholder={`${t('search_placeholder')} ...`}
                        onSearch={(value) => {
                          setSearchTerm(value)
                        }}
                      />
                      <Center position="relative">
                        {errors.selectedArea && (
                          <MotionFlex animate={{ opacity: 1 }} initial={{ opacity: 0 }}>
                            <StateFormIndicator
                              boxPosition={isResult ? 'top' : 'left'}
                              right="110%"
                              top="50%"
                              transform="translateY(-50%)"
                            >
                              {errors.selectedArea}
                            </StateFormIndicator>
                          </MotionFlex>
                        )}

                        <PrimaryButton
                          size="md"
                          isLoading={isSubmitting}
                          type="submit"
                          fontSize="xs"
                          rounded="lg"
                          p={isTabletOrMobile ? 4 : 7}
                          my={1}
                          mx={2}
                          colorScheme="orange"
                          aria-label="Change Theme"
                        >
                          {isTabletOrMobile ? (
                            <VscSearch />
                          ) : (
                            <Text fontSize="xs">{t('search_btn')}</Text>
                          )}
                        </PrimaryButton>
                      </Center>
                    </Flex>
                  </Flex>

                  <SuggestionBox
                    tab={tab}
                    selectItems={selectedArea}
                    results={results}
                    loading={resultsLoading}
                    searchFocus={searchFocus}
                    searchTerm={searchTerm}
                    onClick={(item) => {
                      setFieldValue('selectedArea', uniqBy([...selectedArea, item], '_key'))
                      setFieldValue('search', '')
                      ;(searchInput.current as any).focus()
                    }}
                  />
                </Flex>
                <Filters
                  {...{
                    maxPrice,
                    rooms,
                    minPrice,
                    bedRooms,
                    isResult,
                    bathRooms,
                    rentalTerms,
                    propertyAvailabilities,
                    furnished,
                    propertyTypes,
                    prices,
                    removeFilters,
                    setFieldValue,
                    tab
                  }}
                />
              </Box>
              {!isTabletOrMobile && !shouldShowSearch && (
                <Box position="relative" flex={1} ml={4}>
                  <Image
                    src={images.registerIllustration}
                    position="absolute"
                    draggable={false}
                    width="100%"
                    transform="translateY(-40%)"
                  />
                </Box>
              )}
            </Flex>

            {!isTabletOrMobile && (isDirty || state?.quickSearch) && !isResult ? (
              <AdvancedSearchResults
                tab={tab}
                isDirty={isDirty}
                values={{
                  maxPrice,
                  minPrice,
                  search,
                  bedRooms,
                  bathRooms,
                  rentalTerms,
                  propertyAvailabilities,
                  furnished,
                  propertyTypes,
                  selectedArea
                }}
              />
            ) : (
              <></>
            )}
          </Form>
        )
      }}
    </Formik>
  )
}

SearchBar.defaultProps = {
  rooms: MAX_ROOM_AMOUT,
  prices: generatePrices().arr,
  shouldShowSearch: false
}

export default memo(SearchBar)
